/**
 * Gemini Pro AI service for consultation assistance
 * Provides AI-powered suggestions based on consultation notes and patient history
 */

const GEMINI_API_KEY = 'AIzaSyD-j9NUMpAbLv_JMMKwJKekWezKd4vJTgs'
// Try v1 API (stable) first, fallback to v1beta
const GEMINI_BASE_URL_V1 = 'https://generativelanguage.googleapis.com/v1'
const GEMINI_BASE_URL_V1BETA = 'https://generativelanguage.googleapis.com/v1beta'

export interface GeminiSuggestion {
  type: 'diagnosis' | 'treatment' | 'test' | 'referral' | 'medication' | 'followup'
  title: string
  description: string
  confidence?: 'high' | 'medium' | 'low'
}

import type { MedicationOrderContext, MedicationOrderInsight } from '../types/medicationAI'
import { buildMedicationOrderPrompt } from '../utils/medicationOrderAIPrompt'
import { parseMedicationOrderInsights } from '../utils/medicationOrderAIParse'

export interface ConsultationContext {
  patientAge?: number
  patientGender?: string
  reasonForVisit?: string
  notes?: string
  currentMedications?: string[]
  allergies?: string[]
  chronicConditions?: string[]
  medicalHistory?: string
  diagnosis?: string[]
  vitalSigns?: Record<string, any>
}

export const geminiService = {
  /**
   * Get AI suggestions based on consultation context
   */
  /**
   * List available models (for debugging)
   */
  listModels: async (): Promise<any> => {
    try {
      // Try v1 first
      const response = await fetch(`${GEMINI_BASE_URL_V1}/models?key=${GEMINI_API_KEY}`)
      if (response.ok) {
        return await response.json()
      }
      // Fallback to v1beta
      const responseBeta = await fetch(`${GEMINI_BASE_URL_V1BETA}/models?key=${GEMINI_API_KEY}`)
      return await responseBeta.json()
    } catch (error) {
      console.error('[GeminiService] Error listing models:', error)
      throw error
    }
  },

  /**
   * Get AI suggestions based on consultation context
   */
  getSuggestions: async (context: ConsultationContext): Promise<GeminiSuggestion[]> => {
    try {
      const prompt = buildPrompt(context)
      
      // Try different models and API versions - using available models from the API
      const modelsToTry = ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-2.0-flash', 'gemini-2.0-flash-001']
      const apiVersions = [GEMINI_BASE_URL_V1, GEMINI_BASE_URL_V1BETA]
      
      for (const baseUrl of apiVersions) {
        for (const model of modelsToTry) {
          try {
            // Try with API key in query parameter
            const url = `${baseUrl}/models/${model}:generateContent?key=${GEMINI_API_KEY}`
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                contents: [
                  {
                    parts: [
                      {
                        text: prompt,
                      },
                    ],
                  },
                ],
              }),
            })

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}))
              // If this model doesn't work, try the next one
              if (errorData.error?.message?.includes('not found') || errorData.error?.message?.includes('not supported')) {
                console.warn(`[GeminiService] Model ${model} not available, trying next...`)
                continue
              }
              const errorMessage = errorData.error?.message || `API error: ${response.statusText}`
              console.error('[GeminiService] API Error:', {
                status: response.status,
                statusText: response.statusText,
                error: errorData,
                model,
              })
              throw new Error(errorMessage)
            }

            const data = await response.json()
            const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text || ''

            if (!generatedText) {
              console.warn('[GeminiService] No text in response:', data)
              return []
            }

            // Parse the AI response into structured suggestions
            return parseSuggestions(generatedText)
          } catch (error: any) {
            // If it's not a "model not found" error, throw it
            if (!error.message?.includes('not found') && !error.message?.includes('not supported')) {
              throw error
            }
            // Otherwise, continue to next model
            continue
          }
        }
      }
      
      // If we get here, none of the models worked
      // Try to list available models for better error message
      try {
        const availableModels = await geminiService.listModels()
        console.log('[GeminiService] Available models:', availableModels)
      } catch (listError) {
        console.error('[GeminiService] Could not list models:', listError)
      }
      
      throw new Error('No available Gemini models found. Please check your API key permissions and ensure it has access to Gemini models.')
    } catch (error: any) {
      console.error('[GeminiService] Error getting suggestions:', error)
      // If it's a CORS or network error, provide a helpful message
      if (error.message?.includes('Failed to fetch') || error.message?.includes('CORS')) {
        throw new Error('Unable to connect to AI service. Please check your network connection.')
      }
      throw new Error(`Failed to get AI suggestions: ${error.message || 'Unknown error'}`)
    }
  },

  /**
   * Adjunct explanations / ranked formulary alternatives for prescribing (non-authoritative).
   */
  getMedicationOrderInsights: async (context: MedicationOrderContext): Promise<MedicationOrderInsight> => {
    const prompt = buildMedicationOrderPrompt(context)
    const modelsToTry = ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-2.0-flash', 'gemini-2.0-flash-001']
    const apiVersions = [GEMINI_BASE_URL_V1, GEMINI_BASE_URL_V1BETA]

    for (const baseUrl of apiVersions) {
      for (const model of modelsToTry) {
        try {
          const url = `${baseUrl}/models/${model}:generateContent?key=${GEMINI_API_KEY}`
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt }] }],
              // Note: Do not set responseMimeType here — v1 generateContent rejects it for some models
              // ("Unknown name responseMimeType at generation_config"). Prompt + parser enforce JSON.
              generationConfig: {
                temperature: 0.4,
              },
            }),
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            if (errorData.error?.message?.includes('not found') || errorData.error?.message?.includes('not supported')) {
              continue
            }
            throw new Error(errorData.error?.message || `API error: ${response.statusText}`)
          }

          const data = await response.json()
          const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text || ''
          if (!generatedText) return { explanations: [], rankedAlternatives: [] }
          return parseMedicationOrderInsights(generatedText)
        } catch (error: any) {
          if (!error.message?.includes('not found') && !error.message?.includes('not supported')) {
            throw error
          }
        }
      }
    }
    throw new Error('No available Gemini models for medication order insights.')
  },
}

/**
 * Build a prompt for Gemini based on consultation context
 * NOTE: This context should NEVER contain PII (names, addresses, phone numbers, emails, patient IDs, etc.)
 * Only medical information is included for privacy compliance.
 */
function buildPrompt(context: ConsultationContext): string {
  const parts: string[] = []

  parts.push('You are a medical AI assistant helping a healthcare provider during a patient consultation.')
  parts.push('Based on the following anonymized medical information (no patient identifiers included), provide clinical suggestions in a structured format.')
  parts.push('')

  if (context.patientAge && context.patientGender) {
    parts.push(`Patient: ${context.patientAge}-year-old ${context.patientGender}`)
  }

  if (context.reasonForVisit) {
    parts.push(`Reason for visit: ${context.reasonForVisit}`)
  }

  if (context.notes) {
    parts.push(`Clinical notes: ${context.notes}`)
  }

  if (context.currentMedications && context.currentMedications.length > 0) {
    parts.push(`Current medications: ${context.currentMedications.join(', ')}`)
  }

  if (context.allergies && context.allergies.length > 0) {
    parts.push(`Allergies: ${context.allergies.join(', ')}`)
  }

  if (context.chronicConditions && context.chronicConditions.length > 0) {
    parts.push(`Chronic conditions: ${context.chronicConditions.join(', ')}`)
  }

  if (context.medicalHistory) {
    parts.push(`Medical history: ${context.medicalHistory}`)
  }

  if (context.diagnosis && context.diagnosis.length > 0) {
    parts.push(`Current diagnosis: ${context.diagnosis.join(', ')}`)
  }

  parts.push('')
  parts.push('Provide suggestions in the following JSON format (array of suggestions):')
  parts.push('[')
  parts.push('  {')
  parts.push('    "type": "diagnosis|treatment|test|referral|medication|followup",')
  parts.push('    "title": "Brief title",')
  parts.push('    "description": "Detailed description",')
  parts.push('    "confidence": "high|medium|low"')
  parts.push('  }')
  parts.push(']')
  parts.push('')
  parts.push('Focus on clinically relevant suggestions. Return only valid JSON, no additional text.')

  return parts.join('\n')
}

/**
 * Parse AI response into structured suggestions
 */
function parseSuggestions(text: string): GeminiSuggestion[] {
  try {
    // Try to extract JSON from the response
    const jsonMatch = text.match(/\[[\s\S]*\]/)
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0])
      if (Array.isArray(parsed)) {
        return parsed.filter((s: any) => s.type && s.title && s.description)
      }
    }

    // Fallback: try to parse the entire response as JSON
    const parsed = JSON.parse(text)
    if (Array.isArray(parsed)) {
      return parsed.filter((s: any) => s.type && s.title && s.description)
    }

    return []
  } catch (error) {
    console.error('[GeminiService] Failed to parse suggestions:', error)
    // Return empty array if parsing fails
    return []
  }
}






























